import { backgroundImage, Gray_Light_White, imageSize, resetButton } from '@common/styles';
import { css, SerializedStyles } from '@emotion/react';
import styled from '@emotion/styled';
import { brand_color } from '@common/styles';
import { Gray_Light_100, Gray_Light_200, Gray_Light_900, Pink_Light_100 } from '@croquiscom/zds';
import size1x from './image/white-zpay@1x.png';
import size2x from './image/white-zpay@2x.png';
import size3x from './image/white-zpay@3x.png';
import dbCheckSvg from './image/db-check.svg';
import question from './image/question.svg';
import speechBubble from './image/speech_bubble.svg';
import shipping from './image/shipping.svg';
import plus from './image/plus.svg';
import plusWhite from './image/plus-white.svg';
import minus from './image/minus.svg';
import cancel from './image/cancel.svg';
import xGray600 from './image/x-gray600.svg';
import call from './image/call.svg';
import phoneCall from './image/phonecall.svg';
import mail from './image/mail.svg';
import location from './image/location.svg';
import plusGray400 from './image/plus-gray400.svg';
import minusGray400 from './image/minus-gray400.svg';

export enum FaceButtonColor {
  GRAY100 = 'GRAY100',
  GRAY900 = 'GRAY900',
  BRAND = 'BRAND',
}

export enum FaceButtonSize {
  W24_H24 = 'w24_h24',
  S13_H32 = 's13_h32',
  S13_H40 = 's13_h40',
  S15_H40 = 's15_h40',
  S15_H44 = 's15_h44',
  S15_H48 = 's15_h48',
  S15_H56 = 's15_h56',
}

export enum FaceButtonIcon {
  SPEECH_BUBBLE = 'ic_speech_bubble',
  QUESTION = 'ic_question',
  SHIPPING = 'ic_shipping',
  CALL = 'ic_call',
  CANCEL = 'ic_cancel',
  DB_CHECK = 'ic_db_check',
  LOCATION = 'ic_location',
  MAIL = 'ic_mail',
  MINUS = 'ic_minus',
  PLUS = 'ic_plus',
  PLUS_WHITE = 'ic_plus_white',
  PHONECALL = 'ic_phonecall',
  X_GRAY600 = 'ic_x_gray600',
  WHITE_ZPAY = 'ic_white_zpay',
}

const colors: Record<FaceButtonColor, SerializedStyles> = {
  [FaceButtonColor.GRAY100]: css`
    background-color: ${Gray_Light_100};
    color: ${Gray_Light_900};
  `,
  [FaceButtonColor.GRAY900]: css`
    background-color: ${Gray_Light_900};
    color: ${Gray_Light_White};
  `,
  [FaceButtonColor.BRAND]: css`
    color: ${Gray_Light_White};
    background-color: ${brand_color};
    &:disabled {
      color: ${Gray_Light_White};
      background-color: ${Pink_Light_100};
    }
  `,
};

const sizes: Record<FaceButtonSize, SerializedStyles> = {
  [FaceButtonSize.W24_H24]: css`
    width: 24px;
    height: 24px;
    border-radius: 12px;
  `,
  [FaceButtonSize.S13_H32]: css`
    height: 32px;
    padding: 0 12px;
    border-radius: 16px;
  `,
  [FaceButtonSize.S13_H40]: css`
    height: 40px;
    padding: 0 16px;
    border-radius: 20px;
  `,
  [FaceButtonSize.S15_H40]: css`
    height: 40px;
    border-radius: 20px;
  `,
  [FaceButtonSize.S15_H44]: css`
    height: 44px;
    border-radius: 22px;
  `,
  [FaceButtonSize.S15_H48]: css`
    height: 48px;
    border-radius: 24px;
  `,
  [FaceButtonSize.S15_H56]: css`
    height: 56px;
    border-radius: 28px;
  `,
};

export const Container = styled.button<{ colorType?: FaceButtonColor; sizeType?: FaceButtonSize }>`
  ${resetButton}
  max-width: 100%;
  overflow: hidden;

  &:disabled,
  &.disabled {
    color: ${Gray_Light_200};
    background-color: ${Gray_Light_100};
    cursor: not-allowed;
  }

  ${({ colorType }) => (colorType ? colors[colorType] : `color: ${Gray_Light_900};`)}
  ${({ sizeType }) => sizeType && sizes[sizeType]}

  /* icons */
  > .icon {
    background-color: transparent;
  }

  > .icon,
  > .text {
    display: inline-flex;
  }
  > .text {
    max-width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  > .ic_speech_bubble,
  > .ic_shipping,
  > .ic_white_zpay,
  > .ic_db_check {
    ${imageSize('24px', '24px')}
  }
  > .ic_white_zpay,
  > .ic_db_check {
    margin-right: 4px;
    + .text {
      max-width: calc(100% - 28px);
    }
  }
  > .ic_white_zpay {
    ${backgroundImage({
      size1x,
      size2x,
      size3x,
    })}
  }
  > .ic_db_check {
    ${backgroundImage({
      size1x: dbCheckSvg,
    })}
  }
  > .ic_speech_bubble,
  > .ic_shipping {
    margin-right: 2px;
    + .text {
      max-width: calc(100% - 26px);
    }
  }
  > .ic_white_zpay {
    padding-bottom: 3px;
  }
  > .ic_question {
    background-image: url(${question});
  }
  > .ic_speech_bubble {
    background-image: url(${speechBubble});
  }
  > .ic_shipping {
    background-image: url(${shipping});
  }
  > .ic_call,
  > .ic_phonecall,
  > .ic_mail,
  > .ic_cancel,
  > .ic_plus,
  > .ic_plus_Gray_Light_White,
  > .ic_minus,
  > .ic_x_gray600,
  > .ic_location,
  > .ic_question {
    ${imageSize('16px', '16px')}
  }
  > .ic_call,
  > .ic_phonecall,
  > .ic_mail,
  > .ic_location {
    margin-right: 4px;
  }
  > .ic_plus {
    background-image: url(${plus});
  }
  > .ic_plus_white {
    background-image: url(${plusWhite});
  }
  > .ic_minus {
    background-image: url(${minus});
  }
  > .ic_cancel {
    background-image: url(${cancel});
  }
  > .ic_x_gray600 {
    background-image: url(${xGray600});
  }
  > .ic_call {
    background-image: url(${call});
  }
  > .ic_phonecall {
    background-image: url(${phoneCall});
  }
  > .ic_mail {
    background-image: url(${mail});
  }
  > .ic_location {
    background-image: url(${location});
  }
  &[disabled],
  &.disabled {
    > .ic_plus {
      background-image: url(${plusGray400});
    }
    > .ic_minus {
      background-image: url(${minusGray400});
    }
  }
`;
